// Project-specific replacement for the generated
// src/integrations/supabase/auth-attacher.ts (which must stay untouched).
// The generated middleware statically imports the Supabase client; because
// src/start.ts is part of the client entry graph, that pulled vendor-supabase
// (auth/realtime/storage, ~50 KiB) into the eager bundle on every page.
// This version dynamic-imports the client, so the library only downloads when
// a server function is actually invoked.
import { createMiddleware } from "@tanstack/react-start";

import { getSessionSafe, clearStaleAuthStorage, currentReturnPath } from "./authSession";

const ACCOUNT_PREFIXES = ["/my-account", "/parts-catalog/account"];

function onAccountPage() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.toLowerCase();
  return ACCOUNT_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

function isUnauthorized(error: unknown) {
  if (error == null) return false;
  if (typeof error === "object") {
    const status = (error as { status?: number; statusCode?: number }).status
      ?? (error as { statusCode?: number }).statusCode;
    if (status === 401) return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return message.includes("UNAUTHORIZED") || message.includes("Unauthorized") || message.includes("401");
}

/** Send an expired visitor back to the right sign-in screen instead of retrying. */
function redirectToLogin() {
  if (typeof window === "undefined") return;
  clearStaleAuthStorage();
  const back = encodeURIComponent(currentReturnPath());
  const target = window.location.pathname.toLowerCase().startsWith("/parts-catalog/account")
    ? `/parts-catalog/account/login?next=${back}`
    : `/community-login?redirect=${back}`;
  window.location.replace(target);
}

export const attachSupabaseAuthLazy = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    let token: string | undefined;
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      // Bounded: a stuck token refresh must not hang every server call and make
      // the page look frozen. Without a token the server answers 401 normally.
      const { session } = await getSessionSafe(supabase);
      token = session?.access_token;
    } catch {
      token = undefined;
    }
    try {
      return await next({
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (error) {
      if (isUnauthorized(error) && onAccountPage()) redirectToLogin();
      throw error;
    }
  },
);